
import { GameMode } from '@/contexts/GameModeContext';
import * as CookieUtils from '../cookieUtils';
import { formatDate, getEffectiveDate } from '../dateUtils';

export interface StorageAdapter {
  set(key: string, value: string): void;
  get(key: string): string | null;
  remove(key: string): void;
  getFromMode(key: string, mode: GameMode): string | null; // New method for cross-mode access
}

export class CookieStorageAdapter implements StorageAdapter {
  private prefix: string;
  private mode: GameMode;

  constructor(prefix: string = '', mode: GameMode = 'daily') {
    this.prefix = prefix;
    this.mode = mode;
    console.log(`Created CookieStorageAdapter for mode ${mode} with prefix ${prefix}`);
  }

  set(key: string, value: string): void {
    const options = {
      path: '/',
      secure: window.location.protocol === 'https:' && !window.location.hostname.includes('lovable.dev'),
      sameSite: (window.location.protocol === 'https:' && !window.location.hostname.includes('lovable.dev')) ? 'strict' as const : 'lax' as const,
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
    };
    
    const fullKey = this.getFullKey(key);
    CookieUtils.setCookie(fullKey, value, options);
    console.log(`Storage: SET ${fullKey} for mode ${this.mode} = ${value.substring(0, 50)}${value.length > 50 ? '...' : ''}`);
  }

  get(key: string): string | null {
    const fullKey = this.getFullKey(key);
    const value = CookieUtils.getCookie(fullKey);
    console.log(`Storage: GET ${fullKey} for mode ${this.mode} = ${value ? (value.length > 50 ? value.substring(0, 50) + '...' : value) : 'null'}`);
    return value;
  }

  remove(key: string): void {
    const fullKey = this.getFullKey(key);
    CookieUtils.deleteCookie(fullKey);
    console.log(`Storage: REMOVE ${fullKey} for mode ${this.mode}`);
  }

  // New method to access data from a specific game mode
  getFromMode(key: string, targetMode: GameMode): string | null {
    const modePrefix = targetMode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_';
    const fullKey = `${modePrefix}${key}`;
    const value = CookieUtils.getCookie(fullKey);
    console.log(`Storage: CROSS-MODE GET ${fullKey} from ${targetMode} mode = ${value ? 'exists' : 'null'}`);
    return value;
  }

  private getFullKey(key: string): string {
    // Common keys that should be shared across modes (none for now - mode separation is important)
    const sharedKeys: string[] = [];
    
    // Use the default prefix for shared keys
    if (sharedKeys.includes(key)) {
      return `babblegram_${key}`;
    }
    
    // Use mode-specific prefix for everything else
    return `${this.prefix}${key}`;
  }
}

// Factory to create storage adapters based on game mode
export const createStorageAdapter = (mode: GameMode, config: { storagePrefix: string }): StorageAdapter => {
  return new CookieStorageAdapter(config.storagePrefix, mode);
};

// Create a direct utility function for accessing cookies in overlay manager and other components
export const directCookieAccess = {
  getCookie: (key: string): string | null => {
    const value = CookieUtils.getCookie(key);
    return value;
  },
  
  setCookie: (key: string, value: string): void => {
    const options = {
      path: '/',
      secure: window.location.protocol === 'https:' && !window.location.hostname.includes('lovable.dev'),
      sameSite: (window.location.protocol === 'https:' && !window.location.hostname.includes('lovable.dev')) ? 'strict' as const : 'lax' as const,
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
    };
    CookieUtils.setCookie(key, value, options);
    console.log(`DirectCookieAccess: SET ${key} = ${value.substring(0, 50)}${value.length > 50 ? '...' : ''}`);
  },
  
  deleteCookie: (key: string): void => {
    CookieUtils.deleteCookie(key);
    console.log(`DirectCookieAccess: DELETE ${key}`);
  },
  
  // Add methods to check mode-specific state
  hasInProgressGame: (mode: 'daily' | 'practice'): boolean => {
    const prefix = mode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_';
    const currentWord = CookieUtils.getCookie(`${prefix}current_word`);
    const gameStatus = CookieUtils.getCookie(`${prefix}game_status`);
    const savedDate = CookieUtils.getCookie(`${prefix}game_persistence_date`);
    const today = formatDate(getEffectiveDate());

    // For DAILY mode, only consider in-progress if the saved date matches today
    if (mode === 'daily') {
      if (!savedDate || savedDate !== today) {
        return false;
      }
    }

    const hasGame = !!currentWord && gameStatus === 'playing';
    
    console.log(`DirectCookieAccess: hasInProgressGame for ${mode} = ${hasGame}`, {
      currentWord: !!currentWord,
      gameStatus,
      savedDate,
      today,
      timestamp: new Date().toISOString()
    });
    
    return hasGame;
  },
  
  getGameStatus: (mode: 'daily' | 'practice'): string | null => {
    const prefix = mode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_';
    return CookieUtils.getCookie(`${prefix}game_status`);
  },

  hasCompletedGame: (mode: 'daily' | 'practice'): boolean => {
    const prefix = mode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_';
    const currentWord = CookieUtils.getCookie(`${prefix}current_word`);
    const gameStatus = CookieUtils.getCookie(`${prefix}game_status`);
    const completionState = CookieUtils.getCookie(`${prefix}game_completion_state`);
    const savedDate = CookieUtils.getCookie(`${prefix}game_persistence_date`);
    const today = formatDate(getEffectiveDate());

    // For DAILY mode, completion only counts if the saved date matches today
    if (mode === 'daily') {
      if (!savedDate || savedDate !== today) {
        console.log(`DirectCookieAccess: Ignoring stale completion for ${mode}`, { savedDate, today });
        return false;
      }
    }
    
    const hasCompleted = !!currentWord && (gameStatus === 'won' || gameStatus === 'lost' || completionState === 'true');
    
    console.log(`DirectCookieAccess: hasCompletedGame for ${mode} = ${hasCompleted}`, {
      currentWord: !!currentWord,
      gameStatus,
      completionState,
      savedDate,
      today,
      timestamp: new Date().toISOString()
    });
    
    return hasCompleted;
  },

  // Add method to check for any game (in progress or completed)
  hasAnyGame: (mode: 'daily' | 'practice'): boolean => {
    return directCookieAccess.hasInProgressGame(mode) || directCookieAccess.hasCompletedGame(mode);
  }
};
